import { ExpectedError } from "../../expectedError";
import { basicAuth, oauthAuth } from "../standardAuthPatterns";
import { defineProduct, productVariant } from "../types";
import logo from "./logo.svg";

const zendeskHost = (domain: string | undefined) => {
  const host = domain?.trim().toLowerCase() ?? "";
  return /^[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.zendesk\.com$/.test(host)
    ? host
    : "";
};

const requireZendeskHost = (domain: string | undefined) => {
  const host = zendeskHost(domain);
  if (!host) {
    throw new ExpectedError(
      "Enter your full Zendesk domain, such as yourcompany.zendesk.com."
    );
  }
  return host;
};

export const zendesk = defineProduct({
  name: "Zendesk",
  tagLine: "Customer service and support",
  logo,
  tags: ["featured", "it", "productivity", "communication"],
  variants: [
    productVariant({
      identifier: "zendesk",
      authLabel: "API token",
      keywords: "tickets helpdesk agents macros organizations customer service",
      protocol: "http",
      defaultUrlPattern: () => "https://[^/]*\\.zendesk\\.com/.*",
      auth: (app) =>
        basicAuth({
          username: `${app.config.email}/token`,
          password: app.secret.apiToken,
        }),
      steps: [
        {
          title: "Get an API token",
          items: [
            {
              kind: "markdown",
              content:
                "Visit your Zendesk admin panel. Navigate to **Admin Center > Apps and integrations > Zendesk API > Zendesk API settings** and enable token access. Then create a new API token.",
            },
          ],
        },
        {
          title: "Enter your credentials",
          items: [
            {
              kind: "hostname",
              label: "Zendesk domain",
              required: true,
              placeholder: "yourcompany",
              target: { kind: "configuration", field: "domain" },
              helperText: "Your Zendesk domain (e.g., yourcompany.zendesk.com)",
            },
            {
              kind: "input",
              label: "Email address",
              required: true,
              target: { kind: "configuration", field: "email" },
              helperText:
                "The email address associated with your Zendesk account",
            },
            {
              kind: "input",
              label: "API token",
              required: true,
              target: { kind: "secrets", field: "apiToken" },
            },
          ],
        },
      ],
      connectionTest: {
        protocol: "http",
        request: ({ domain }) => `https://${domain}/api/v2/users/me.json`,
      },
      publicEnvVars: ({ domain, email }) => ({
        ZENDESK_URL: domain ? `https://${domain}` : "",
        ZENDESK_EMAIL: email || "",
      }),
    }),
    productVariant({
      identifier: "zendeskOAuth",
      authLabel: "OAuth 2.0 (client credentials)",
      keywords: "tickets helpdesk agents macros organizations customer service",
      protocol: "http",
      defaultUrlPattern: ({ domain }) => {
        if (!domain?.trim()) return "https://[^/]*\\.zendesk\\.com/api/v2/.*";
        const host = zendeskHost(domain);
        return host ? `https://${host.replace(/\./g, "\\.")}/api/v2/.*` : "";
      },
      auth: oauthAuth,
      steps: [
        {
          title: "Enter your Zendesk domain",
          items: [
            {
              kind: "hostname",
              label: "Zendesk domain",
              required: true,
              placeholder: "yourcompany.zendesk.com",
              target: { kind: "configuration", field: "domain" },
              helperText:
                "Enter the full Zendesk Support domain, without https:// or a path.",
            },
          ],
        },
        {
          title: "Create a confidential OAuth client",
          items: [
            {
              kind: "markdown",
              content:
                "In Zendesk **Admin Center → Apps and integrations → APIs → OAuth clients**, create a dedicated client and select **Confidential** as the client kind. Creating a client requires an admin or an agent with the **Manage APIs** permission. Copy its **Identifier** and **Secret** after saving; the secret is shown only once. No redirect URL is needed for this connection type.\n\nThis connector uses the client credentials flow: requests act as the Zendesk user who created the OAuth client, not a user who signs in later. Give that user only the permissions needed for your workflows, and configure the client’s **Allowed scopes** to match the access you need. This option is for the Zendesk Support API, not Zendesk Sell or Sunshine Conversations.",
            },
          ],
        },
        {
          title: "Enter your OAuth credentials and scopes",
          items: [
            {
              kind: "input",
              label: "Client ID (Identifier)",
              required: true,
              target: { kind: "oauthConnection", field: "clientId" },
            },
            {
              kind: "input",
              label: "Client secret",
              required: true,
              target: { kind: "oauthConnection", field: "clientSecret" },
            },
            {
              kind: "input",
              label: "Scopes",
              required: true,
              placeholder: "tickets:read",
              target: { kind: "oauthConnection", field: "scopes" },
              helperText:
                "Enter a space-separated list of the narrowest scopes your workflows need. The requested scopes must be within the client’s Allowed scopes.",
            },
            {
              kind: "markdown",
              content:
                "Choose [scopes](https://developer.zendesk.com/api-reference/ticketing/oauth/grant_type_tokens/#scope) based on the API requests your workflows make; for example, `tickets:read` for reading tickets. The connection test reads only the current OAuth token’s properties and does not require ticket access or an extra administrator role. A successful test does not establish access to other API endpoints.\n\n3B requests new access tokens automatically when they expire. Zendesk does not issue refresh tokens for the client credentials flow.",
            },
          ],
        },
      ],
      oauth: {
        grantType: () => "client_credentials",
        tokenEndpoint: ({ domain }) =>
          `https://${requireZendeskHost(domain)}/oauth/tokens`,
        tokenEndpointAuthMethod: () => "client_secret_post",
        tokenRequestFormat: () => "json",
        clientCredentialsFrom: "db",
        scopes: () => [],
        normalizeTokenRequest: (parameters) => {
          if (!parameters.scope?.trim()) {
            throw new Error("Enter at least one OAuth scope.");
          }
          return parameters;
        },
      },
      connectionTest: {
        protocol: "http",
        request: ({ domain }) => ({
          url: `https://${requireZendeskHost(domain)}/api/v2/oauth/tokens/current`,
          method: "GET",
          expectedStatus: 200,
          expectedBodyContains: ['"token"', '"client_id"', '"user_id"'],
        }),
      },
      publicEnvVars: ({ domain }) => {
        const host = zendeskHost(domain);
        return {
          ZENDESK_URL: host ? `https://${host}` : "",
          ZENDESK_EMAIL: "",
        };
      },
    }),
  ],
});
